/**
 * VK Delivery Zone Rescue Flow v2.1
 * --------------------------------
 * - Shows catering options when delivery unavailable
 * - Hides "Loading pickup time..." bug
 * - Better spacing to not overlap Checkout button
 */

(() => {
  // Prevent double-binding
  if (window.__VK_DELIVERY_RESCUE_BOUND) return;
  window.__VK_DELIVERY_RESCUE_BOUND = true;
  window.__VK_DELIVERY_RESCUE_VERSION = "v2.1";

  // ======================
  // CONFIGURATION
  // ======================
  const CONFIG = {
    notAvailableText: 'Delivery not available',
    loadingText: 'Loading pickup time',
    corporateUrl: '/pages/corporate-catering-london',
    partyUrl: '/pages/party-at-home',
    minOrder: '£500',
  };

  // ======================
  // UTILITY FUNCTIONS
  // ======================
  
  const findElementByText = (text, container = document.body) => {
    const walker = document.createTreeWalker(
      container,
      NodeFilter.SHOW_TEXT,
      null,
      false
    );
    
    let node;
    while (node = walker.nextNode()) {
      if (node.textContent && node.textContent.includes(text)) {
        return node.parentElement;
      }
    }
    return null;
  };

  /**
   * Hide stuck "Loading pickup time..." elements - more aggressive
   */
  const hideLoadingPickupTime = () => {
    // Method 1: Find by text
    const loadingEl = findElementByText(CONFIG.loadingText);
    if (loadingEl) {
      // Hide it and all parent containers up to 3 levels
      let el = loadingEl;
      for (let i = 0; i < 3; i++) {
        if (el) {
          el.style.cssText = 'display: none !important; visibility: hidden !important; height: 0 !important; overflow: hidden !important;';
          // Only go up if parent isn't a major container
          if (el.parentElement && 
              !el.parentElement.matches('body, main, section, article, .cart, [class*="cart"]')) {
            el = el.parentElement;
          } else {
            break;
          }
        }
      }
    }
    
    // Method 2: Look for any element with this text content directly
    document.querySelectorAll('*').forEach(el => {
      if (el.childNodes.length === 1 && 
          el.textContent?.trim().toLowerCase().includes('loading pickup time')) {
        el.style.cssText = 'display: none !important;';
      }
    });
  };

  const isDeliveryUnavailable = () => {
    const textEl = findElementByText(CONFIG.notAvailableText);
    if (textEl) {
      const style = window.getComputedStyle(textEl);
      if (style.display !== 'none' && style.visibility !== 'hidden') {
        return textEl;
      }
    }
    return null;
  };

  const getEnteredPostcode = () => {
    const inputs = document.querySelectorAll('input[type="text"]');
    for (const input of inputs) {
      const value = input.value?.trim();
      if (value && /^[A-Z]{1,2}\d[A-Z\d]?\s*\d[A-Z]{2}$/i.test(value)) {
        return value.toUpperCase();
      }
    }
    return '';
  };

  // ======================
  // INJECT STYLES
  // ======================
  
  const injectStyles = () => {
    if (document.getElementById('vk-rescue-styles')) return;
    
    const styles = document.createElement('style');
    styles.id = 'vk-rescue-styles';
    styles.textContent = `
      /* Hide loading pickup time via CSS too */
      [class*="loading-pickup"],
      [class*="pickup-loading"] {
        display: none !important;
      }
      
      .vk-rescue-banner {
        background: linear-gradient(135deg, #f8fdf9 0%, #f0faf8 100%);
        border: 2px solid #6BCABA;
        border-radius: 14px;
        padding: 20px;
        margin: 16px 0 20px 0; /* Added bottom margin */
        animation: vk-rescue-fade-in 0.3s ease;
      }
      
      @keyframes vk-rescue-fade-in {
        from { opacity: 0; transform: translateY(-8px); }
        to { opacity: 1; transform: translateY(0); }
      }
      
      .vk-rescue-banner__header {
        display: flex;
        align-items: flex-start;
        gap: 12px;
        margin-bottom: 14px;
      }
      
      .vk-rescue-banner__icon {
        font-size: 24px;
        line-height: 1;
        flex-shrink: 0;
      }
      
      .vk-rescue-banner__title {
        font-size: 1rem;
        font-weight: 700;
        color: #1a1a1a;
        margin: 0 0 4px;
        line-height: 1.3;
      }
      
      .vk-rescue-banner__subtitle {
        font-size: 0.85rem;
        color: #555;
        margin: 0;
        line-height: 1.4;
      }
      
      .vk-rescue-banner__buttons {
        display: flex;
        flex-direction: column;
        gap: 8px;
      }
      
      .vk-rescue-btn {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        gap: 8px;
        padding: 12px 16px;
        border-radius: 10px;
        font-size: 0.9rem;
        font-weight: 600;
        text-decoration: none;
        cursor: pointer;
        transition: transform 0.15s ease, box-shadow 0.15s ease;
        border: none;
        width: 100%;
      }
      
      .vk-rescue-btn:hover {
        transform: translateY(-1px);
        text-decoration: none;
      }
      
      .vk-rescue-btn--corporate {
        background: linear-gradient(135deg, #309B42 0%, #268a3a 100%);
        color: #fff !important;
        box-shadow: 0 2px 8px rgba(48, 155, 66, 0.25);
      }
      
      .vk-rescue-btn--corporate:hover {
        box-shadow: 0 4px 12px rgba(48, 155, 66, 0.35);
        color: #fff !important;
      }
      
      .vk-rescue-btn--party {
        background: linear-gradient(135deg, #6BCABA 0%, #5ab5a6 100%);
        color: #fff !important;
        box-shadow: 0 2px 8px rgba(107, 202, 186, 0.25);
      }
      
      .vk-rescue-btn--party:hover {
        box-shadow: 0 4px 12px rgba(107, 202, 186, 0.35);
        color: #fff !important;
      }
      
      .vk-rescue-btn__icon {
        font-size: 1.1em;
      }
      
      .vk-rescue-banner__note {
        font-size: 0.75rem;
        color: #777;
        margin: 12px 0 0;
        text-align: center;
      }
    `;
    
    document.head.appendChild(styles);
  };

  // ======================
  // CREATE RESCUE BANNER
  // ======================
  
  const createRescueBanner = (postcode) => {
    const banner = document.createElement('div');
    banner.id = 'vk-rescue-banner';
    banner.className = 'vk-rescue-banner';
    
    const postcodeText = postcode ? ` to <strong>${postcode}</strong>` : '';
    
    banner.innerHTML = `
      <div class="vk-rescue-banner__header">
        <span class="vk-rescue-banner__icon">📍</span>
        <div>
          <p class="vk-rescue-banner__title">Outside our standard delivery zone?</p>
          <p class="vk-rescue-banner__subtitle">No problem! We deliver catering orders${postcodeText} across London. Send us an enquiry and we'll confirm availability.</p>
        </div>
      </div>
      
      <div class="vk-rescue-banner__buttons">
        <a href="${CONFIG.corporateUrl}" class="vk-rescue-btn vk-rescue-btn--corporate">
          <span class="vk-rescue-btn__icon">🏢</span>
          <span>Corporate & Office</span>
        </a>
        <a href="${CONFIG.partyUrl}" class="vk-rescue-btn vk-rescue-btn--party">
          <span class="vk-rescue-btn__icon">🎉</span>
          <span>Party at Home</span>
        </a>
      </div>
      
      <p class="vk-rescue-banner__note">Minimum order from ${CONFIG.minOrder} · Delivered from St Paul's (EC4V)</p>
    `;
    
    return banner;
  };

  // ======================
  // INJECT BANNER
  // ======================
  
  let bannerInjected = false;
  
  const injectRescueBanner = (targetEl, postcode) => {
    if (document.getElementById('vk-rescue-banner')) return;
    
    injectStyles();
    
    const banner = createRescueBanner(postcode);
    
    // Find the delivery note textarea and insert after it
    // This positions it above the Checkout button properly
    const deliveryNote = document.querySelector('textarea[placeholder*="special instructions"], textarea[placeholder*="delivery"]');
    
    if (deliveryNote) {
      // Insert after the delivery note's parent container
      let insertPoint = deliveryNote;
      for (let i = 0; i < 2; i++) {
        if (insertPoint.parentElement) {
          insertPoint = insertPoint.parentElement;
        }
      }
      insertPoint.parentNode.insertBefore(banner, insertPoint.nextSibling);
    } else {
      // Fallback: insert after the unavailable message
      let insertPoint = targetEl;
      for (let i = 0; i < 3; i++) {
        if (insertPoint.parentElement && 
            insertPoint.parentElement.tagName !== 'BODY' &&
            insertPoint.parentElement.tagName !== 'FORM') {
          insertPoint = insertPoint.parentElement;
        }
      }
      insertPoint.parentNode.insertBefore(banner, insertPoint.nextSibling);
    }
    
    bannerInjected = true;
  };

  // ======================
  // MAIN CHECK FUNCTION
  // ======================
  
  let lastUnavailableState = false;
  
  const checkAndRescue = () => {
    // Always try to hide loading pickup time
    hideLoadingPickupTime();
    
    // Check for unavailable state
    const unavailableEl = isDeliveryUnavailable();
    
    if (unavailableEl && !lastUnavailableState) {
      lastUnavailableState = true;
      const postcode = getEnteredPostcode();
      injectRescueBanner(unavailableEl, postcode);
    } else if (!unavailableEl && lastUnavailableState) {
      lastUnavailableState = false;
      document.getElementById('vk-rescue-banner')?.remove();
      bannerInjected = false;
    }
  };

  // ======================
  // OBSERVER & INIT
  // ======================
  
  const observer = new MutationObserver(() => {
    checkAndRescue();
  });

  const init = () => {
    checkAndRescue();
    
    observer.observe(document.body, {
      childList: true,
      subtree: true,
      characterData: true,
    });
    
    // More frequent checks to catch the loading text
    setInterval(checkAndRescue, 1000);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  window.VKDeliveryRescue = {
    check: checkAndRescue,
    getPostcode: getEnteredPostcode,
    config: CONFIG,
  };
  
  console.log('[VK] Delivery Rescue v2.1 loaded');
})();